import ArrowLeft from "@/svg/ArrowLeft";
import ChatBubble from "@/svg/ChatBubble";
import Send from "@/svg/Send";
import { ChatMessage, TagFilterBar } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useRef, useState } from "react";
import type { ClinicalNoteResponse } from "../../../api/clinicalTrialsApiTypes";
import ToolTipUsingPortal from "../../../components/toolTipUsingPortal/toolTipUsingPortal";
import {
  plainTextToRichText,
  richTextToPlainText,
} from "../../../utils/richText";
import "./PatientChat.scss";

interface PatientChatProps {
  note: ClinicalNoteResponse;
  isSaving: boolean;
  error: string | null;
  onBack: () => void;
  onReply: (body: ReturnType<typeof plainTextToRichText>) => Promise<void>;
  onResolve: () => Promise<void>;
}

function userLabel(user: ClinicalNoteResponse["creator"]): string {
  return user.display_name || user.email || "Unknown";
}

function initialsFromLabel(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return label.slice(0, 2).toUpperCase() || "?";
}

function formatChatTimestamp(timestamp: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

/** Display the selected note and its replies as a docked conversation. */
const PatientChat = ({
  note,
  isSaving,
  error,
  onBack,
  onReply,
  onResolve,
}: PatientChatProps) => {
  const [messageDraft, setMessageDraft] = useState("");
  const [showResolveTooltip, setShowResolveTooltip] = useState(false);
  const resolveButtonRef = useRef<HTMLDivElement>(null);
  const recipientTags = useMemo(
    () =>
      Array.from(
        new Set(
          [note.creator, note.assignee]
            .filter((user): user is ClinicalNoteResponse["creator"] =>
              Boolean(user),
            )
            .map(userLabel),
        ),
      ),
    [note.assignee, note.creator],
  );
  const replies = note.activities.filter(
    (activity) =>
      activity.activity_type === "reply" &&
      activity.body &&
      !activity.deleted_at,
  );

  const sendReply = async (): Promise<void> => {
    const trimmedMessage = messageDraft.trim();
    if (!trimmedMessage || isSaving) return;
    await onReply(plainTextToRichText(trimmedMessage));
    setMessageDraft("");
  };

  return (
    <div className="patient-chat--container flex flex-col overflow-hidden h-full">
      <div className="patient-header h-[3.75rem] p-3 bg-white border-b border-primaryGray-14 flex items-center gap-2">
        <button
          type="button"
          className="icon border-0 bg-transparent p-0 cursor-pointer"
          aria-label="Back to patient notes"
          onClick={onBack}
        >
          <ArrowLeft />
        </button>
        <div
          className="text min-w-0 truncate text-sm font-semibold"
          title={note.title}
        >
          {note.title}
        </div>
      </div>
      <div className="patient-content bg-white flex-1 overflow-auto p-4 flex flex-col gap-4 items-end justify-end">
        <ChatMessage
          senderName={userLabel(note.creator)}
          timestamp={formatChatTimestamp(note.created_at)}
          message={richTextToPlainText(note.body)}
          side="left"
          avatarLabel={initialsFromLabel(userLabel(note.creator))}
          maxWidth="36rem"
        />
        {replies.map((activity) => {
          const sender = userLabel(activity.actor);
          return (
            <ChatMessage
              key={activity.activity_id}
              senderName={sender}
              timestamp={formatChatTimestamp(activity.created_at)}
              message={richTextToPlainText(activity.body!)}
              side="left"
              avatarLabel={initialsFromLabel(sender)}
              maxWidth="36rem"
            />
          );
        })}
      </div>
      <div className="patient-chat-footer bg-white p-4 border-t border-primaryGray-14">
        {recipientTags.length ? (
          <TagFilterBar
            tags={recipientTags}
            showDropdown={false}
            singleLineScrollable
            className="border-0 p-0"
          />
        ) : null}
        {error ? (
          <p className="mt-2 text-x-tiny text-statusRed-text">{error}</p>
        ) : null}
        <form
          className="flex items-center gap-2 mt-3 bg-white rounded border border-primaryGray-14 px-3"
          onSubmit={(event) => {
            event.preventDefault();
            void sendReply();
          }}
        >
          <input
            type="text"
            value={messageDraft}
            onChange={(event) => setMessageDraft(event.target.value)}
            placeholder="Write a message..."
            aria-label="Reply to note"
            className="h-10 flex-1  text-[0.75rem] font-normal leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-2 outline-none placeholder:text-primaryGray-7"
          />
          <div className="actions flex gap-3">
            {note.status === "open" ? (
              <div
                ref={resolveButtonRef}
                className="icon-1 flex items-center"
                onMouseEnter={() => setShowResolveTooltip(true)}
                onMouseLeave={() => setShowResolveTooltip(false)}
              >
                <button
                  type="button"
                  className="border-0 bg-transparent p-0 hover:scale-105 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Mark As Resolved"
                  disabled={isSaving}
                  onFocus={() => setShowResolveTooltip(true)}
                  onBlur={() => setShowResolveTooltip(false)}
                  onClick={() => void onResolve()}
                >
                  <ChatBubble />
                </button>
                <ToolTipUsingPortal
                  placement="top-right"
                  showTooltip={showResolveTooltip}
                  parentRef={resolveButtonRef}
                >
                  Mark As Resolved
                </ToolTipUsingPortal>
              </div>
            ) : null}
            <button
              type="submit"
              className="icon-2 border-0 bg-transparent p-0 hover:scale-105 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Send reply"
              disabled={!messageDraft.trim() || isSaving}
            >
              <Send />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientChat;
